// Wave 3B — Commit 3ab
// Per-result badge that surfaces the nearest brand stockist for an item.
// Prefers tier_1 (mono-brand store) over tier_2 (department store).
// Distance shown in miles, rounded to 1dp.

import { MapPin } from "lucide-react";
import {
  pickNearestStore,
  type OutfitItemOfflineShopping,
} from "@/hooks/useOutfitItems";

interface OfflineShoppingBadgeProps {
  item: Partial<OutfitItemOfflineShopping> | null | undefined;
  onTap?: () => void;
  compact?: boolean;
}

function metresToMiles(m: number | null | undefined): number | null {
  if (m == null || !Number.isFinite(m)) return null;
  return m / 1609.34;
}

function formatDistance(store: {
  distance_m?: number | null;
  distance_mi?: number | null;
}): string {
  const mi =
    typeof store.distance_mi === "number"
      ? store.distance_mi
      : metresToMiles(store.distance_m ?? null);
  if (mi == null) return "nearby";
  if (mi < 0.1) return "< 0.1 mi";
  return `${mi.toFixed(1)} mi`;
}

export function OfflineShoppingBadge({
  item,
  onTap,
  compact,
}: OfflineShoppingBadgeProps) {
  const nearest = pickNearestStore(item);
  if (!nearest) return null;
  const isTier1 =
    Array.isArray(item?.offline_shopping_tier_1) &&
    item!.offline_shopping_tier_1!.some(
      (s) => s.place_id === nearest.place_id || s.name === nearest.name,
    );

  const label = `${nearest.name} · ${formatDistance(nearest)}`;

  return (
    <button
      type="button"
      onClick={onTap}
      aria-label={`Available at ${label}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: compact ? "3px 8px" : "5px 10px",
        borderRadius: 999,
        background: isTier1 ? "#FAECE7" : "#F4F1EA",
        border: `1px solid ${isTier1 ? "#E8C4B6" : "#E4DFD5"}`,
        color: "#2C2820",
        fontSize: compact ? 11 : 12,
        fontWeight: 600,
        lineHeight: 1,
        cursor: onTap ? "pointer" : "default",
      }}
      data-tier={isTier1 ? "tier_1" : "tier_2"}
    >
      <MapPin size={compact ? 11 : 12} strokeWidth={2.25} />
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 180,
        }}
      >
        {label}
      </span>
    </button>
  );
}

export default OfflineShoppingBadge;
